import { t } from 'i18next'
import React, { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import styled from 'styled-components'
import { useCanBlockNumber } from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { Header } from '~/components/compositional/DetailViewStyles'
import { Edit, NewCall } from '~/components/compositional/QuickActions'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import MangoPlusAds from '~/components/MangoPlusAds'
import { EmptyDetailView } from '~/components/shared/EmptyDetailView'
import { ToggleBlockedNumberButton } from '~/components/specialized/BlockedNumber/ToggleBlockedNumberButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { ApplicationRoutes } from '~/hooks/useRouting'
import MessageThreadListView from '~/pages/Messaging/MessageThreadsListView'
import MessageThreadView, {
  MessageThreadHeader,
} from '~/pages/Messaging/MessageThreadView'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'
import { getValidNumber, phoneFormatter } from '~/utils/formatter'
import { showErrorToast } from '~/utils/toast'
import { useMessageThreadsViewModel } from '~/viewModels/Messaging/MessageThreadsViewModel'

import { MessageGroup, MessageThread } from '@mangovoice/types'

const MessageThreadViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

const MessagingPage = () => {
  const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)
  const {
    callModal: [, setCallModal],
  } = useModalState()
  const { messagingEnabled, pbxId } = useUserState()
  const showTexting = useShowTexting()
  const [selectedThread, setSelectedThread] = useState<MessageThread>()
  const [selectedMessageGroup, setSelectedMessageGroup] =
    useState<MessageGroup>()
  const [{ isEditing, newThreadName }, setEditThreadForm] = useState({
    isEditing: false,
    newThreadName: selectedThread?.name,
  })
  const canBlockNumber = useCanBlockNumber(
    selectedThread?.external_phone_number,
  )
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [pendingThreadId, setPendingThreadId] = useState<number>()

  const { togglePanelVisibility } = useTogglePanel()

  useEffect(
    function redirectWhenTextingHidden() {
      // Guards direct navigation and deep links when texting is disabled for
      // the customer in Mango Admin.
      if (!showTexting) {
        navigate(ApplicationRoutes.HOME, { replace: true })
      }
    },
    [showTexting, navigate],
  )

  useEffect(() => {
    setSelectedThread(undefined)
    setSelectedMessageGroup(undefined)
    setPendingThreadId(undefined)
    setEditThreadForm({
      isEditing: false,
      newThreadName: undefined,
    })
    togglePanelVisibility(true)
  }, [pbxId, togglePanelVisibility])

  useEffect(() => {
    if (!selectedThread) {
      togglePanelVisibility(true)
    }
  }, [selectedThread, togglePanelVisibility])

  const { renameThread } = useMessageThreadsViewModel()

  useEffect(() => {
    setEditThreadForm({
      isEditing: false,
      newThreadName: selectedThread?.name,
    })
  }, [selectedThread?.name])

  useEffect(
    function handleDeepLinkThreadId() {
      const threadId = searchParams.get('threadId')
      if (threadId) {
        const id = parseInt(threadId)
        setPendingThreadId(id)
        navigate(ApplicationRoutes.MESSAGING, { replace: true })
      }
    },
    [searchParams, navigate],
  )

  const getSubheadingText = () => {
    if (selectedMessageGroup) {
      return t('messagingPage.subheadingText', {
        phoneNumber: selectedThread?.external_phone_number,
      })
    }
    return `${phoneFormatter(selectedThread?.external_phone_number ?? '')}`
  }

  const handleMessageThreadSelected = (
    thread: MessageThread,
    messageGroup?: MessageGroup,
  ) => {
    setSelectedThread(thread)
    setSelectedMessageGroup(messageGroup)
    togglePanelVisibility(false)
    // Clear pending thread ID if the selected thread matches the pending one
    if (pendingThreadId && thread.id === pendingThreadId) {
      setPendingThreadId(undefined)
    }
  }

  const handleEditThread = () => {
    setEditThreadForm((prevEditThreadForm) => ({
      ...prevEditThreadForm,
      isEditing: false,
    }))

    if (selectedThread && newThreadName) {
      renameThread({
        threadId: selectedThread.id,
        name: newThreadName,
      })
    }
    track(MessagingAnalyticsEvents.ThreadRenameRequest)
  }

  const getMessageThreadViewOrUnselected = () => {
    let threadHeader: ReactNode
    let threadView: ReactNode

    if (selectedThread) {
      if (!messagingEnabled) {
        threadHeader = <Header />
        threadView = (
          <MangoPlusAds
            onUpgrade={() => {
              track(MessagingAnalyticsEvents.UpgradeButtonClicked)
            }}
          />
        )
      } else {
        threadHeader = (
          <MessageThreadHeader
            currentThreadName={selectedThread?.name}
            messageGroupName={selectedMessageGroup?.name}
            subheadingText={getSubheadingText()}
            isEditing={isEditing}
            newThreadName={newThreadName}
            onChangeThreadName={(newThreadName) => {
              setEditThreadForm((prevEditThreadForm) => ({
                ...prevEditThreadForm,
                newThreadName,
              }))
            }}
            onSubmitNewThreadName={handleEditThread}
            onCancelEdit={() => {
              setEditThreadForm((prevEditThreadForm) => ({
                ...prevEditThreadForm,
                isEditing: false,
              }))
            }}
          />
        )
        threadView = (
          <MessageThreadViewWrapper>
            <MessageThreadView currentThread={selectedThread} />
          </MessageThreadViewWrapper>
        )
      }
    } else {
      threadHeader = null
      threadView = (
        <EmptyDetailView message={t('messagingPage.emptyDetailViewMessage')} />
      )
    }

    return { threadHeader, threadView }
  }
  const getMessageQuickActions = () => {
    const quickActions: React.ReactElement[] = []
    quickActions.push(
      <Edit
        key="edit"
        onClick={() => {
          setEditThreadForm((prevEditThreadForm) => ({
            ...prevEditThreadForm,
            isEditing: true,
          }))
        }}
      />,
    )
    if (selectedThread?.external_phone_number) {
      quickActions.push(
        <NewCall
          key="call"
          onClick={() => {
            const number = getValidNumber(selectedThread.external_phone_number)

            if (number.length < 10) {
              showErrorToast({
                message: t('messagingPage.errorToastMessage'),
              })
              return
            }

            track(MessagingAnalyticsEvents.QuickActionCall)

            setCallModal({ open: true, callee: number })
          }}
        />,
      )

      if (canBlockNumber) {
        quickActions.push(
          <ToggleBlockedNumberButton
            key="toggle-blocked"
            phoneNumber={selectedThread.external_phone_number}
          />,
        )
      }
    }
    return quickActions
  }

  const { threadHeader, threadView } = getMessageThreadViewOrUnselected()

  if (!showTexting) {
    return null
  }

  return (
    <ResponsivePage
      listPanel={
        <MessageThreadListView
          onMessageThreadSelected={handleMessageThreadSelected}
          pendingThreadId={pendingThreadId}
          selectedThreadId={selectedThread?.id}
        />
      }
      detailPanel={threadView}
      header={threadHeader}
      quickActionButtons={getMessageQuickActions()}
    />
  )
}

export default MessagingPage
